<template>
  <div class="w-full flex flex-col gap-y-2 text-left h-80">
    <div class="flex flex-col gap-6">
      <v-text-field
        :model-value="search"
        @update:model-value="$emit('update:search', $event)"
        placeholder="Buscar política"
        :append-inner-icon="mdiMagnify"
        density="compact"
        variant="outlined"
        hide-details
        class="w-full md:w-80 h-8"
        color="primary20"
      />

      <div class="flex items-center justify-between flex-wrap gap-4">
        <Legend :items="legendItems" class="text-sm text-gray-600" />

        <outline-button
          v-if="canCreate"
          text="Nova política"
          :append-icon="mdiPlus"
          class="!px-4 !py-1 text-sm !w-[160px]"
          @click="$emit('create')"
        />
      </div>
    </div>

    <div class="w-full text-sm">
      <div class="grid grid-cols-12 py-1 font-semibold px-2 text-gray-700">
        <div class="col-span-4 font-bold text-md text-black">Nome</div>
        <div class="col-span-3 font-bold text-md text-black">Período</div>
        <div class="col-span-3 font-bold text-md text-black">Criado por</div>
        <div class="col-span-2 text-right px-4">Ações</div>
      </div>

      <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mt-6" />

      <div
        v-for="(policy, index) in policies"
        :key="index"
        class="grid grid-cols-12 items-center px-2 py-3 border-b"
        :class="{ 'text-gray-300': !policy.isActive }"
      >
        <div class="col-span-4 flex items-center gap-2">
          <span
            v-if="getVigencyDotClasses(policy)"
            :data-testid="`vigency-dot-${policy.vigencyStatus ?? 'active'}`"
            class="w-2.5 h-2.5 rounded-full border inline-block"
            :class="getVigencyDotClasses(policy)"
          ></span>
          <span>{{ policy.name }}</span>
        </div>

        <div class="col-span-3">{{ formatPeriod(policy) }}</div>

        <div class="col-span-3">{{ policy.createdBy?.name ?? '-' }}</div>

        <div class="col-span-2 flex items-center justify-end gap-3 px-2">
          <v-tooltip v-if="canEdit" text="Editar">
            <template #activator="{ props }">
              <EditIcon
                v-bind="props"
                :color="policy.isActive ? '#2f7f91' : '#D1D5DB'"
                class="cursor-pointer w-5 h-5"
                @click="policy.isActive && $emit('edit', policy)"
              />
            </template>
          </v-tooltip>

          <v-tooltip :text="policy.isActive ? 'Desativar' : 'Ativar'">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps" class="inline-flex">
                <DsSwitch
                  :model-value="policy.isActive ?? false"
                  :loading="loadingSwitches.has(Number(policy.id))"
                  @update:model-value="
                    (val: boolean) => $emit('toggle-status', Number(policy.id), val)
                  "
                />
              </span>
            </template>
          </v-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiPlus, mdiMagnify } from '@mdi/js'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import EditIcon from '@/assets/icons/edit.vue'
import Legend from '@/components/designSystem/Legend/Legend.vue'
import { DSAttendanceVigencyStatus } from '@/constants/ds-attendance.constants'

interface LegendItem {
  label: string
  borderColor: string
  bgColor: string
}

interface BasePolicyItem {
  id?: number | string
  name: string
  isActive?: boolean
  vigencyStatus?: DSAttendanceVigencyStatus
  isInEffect?: boolean
  createdBy?: { name: string } | null
  links?: { isActive?: boolean; startDate: string | Date | null; endDate?: string | Date | null }[]
}

withDefaults(
  defineProps<{
    search: string
    policies: BasePolicyItem[]
    isLoading: boolean
    loadingSwitches: Set<number>
    canCreate: boolean
    canEdit: boolean
    legendItems?: LegendItem[]
  }>(),
  {
    legendItems: () => [
      { label: 'Em vigência', borderColor: 'border-green-500', bgColor: 'bg-green-100' }
    ]
  }
)

const VIGENCY_DOT_MAP: Partial<Record<DSAttendanceVigencyStatus, string>> = {
  [DSAttendanceVigencyStatus.IN_EFFECT]: 'border-green-500 bg-green-100',
  [DSAttendanceVigencyStatus.EXPIRED]: 'border-red-500 bg-red-100',
  [DSAttendanceVigencyStatus.FUTURE]: 'border-blue-500 bg-blue-100'
}

const getVigencyDotClasses = (policy: {
  vigencyStatus?: DSAttendanceVigencyStatus
  isInEffect?: boolean
}): string | null => {
  if (policy.vigencyStatus) {
    return VIGENCY_DOT_MAP[policy.vigencyStatus] ?? null
  }
  if (policy.isInEffect) {
    return VIGENCY_DOT_MAP[DSAttendanceVigencyStatus.IN_EFFECT]!
  }
  return null
}

defineEmits(['update:search', 'create', 'edit', 'toggle-status'])

const formatDateToBR = (value: string | Date | null | undefined): string => {
  if (!value) return '-'
  const date = value instanceof Date ? value : new Date(value)
  if (isNaN(date.getTime())) return '-'
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

const formatPeriod = (policy: BasePolicyItem): string => {
  const activeLink =
    policy.links?.find((link) => link.isActive) ?? policy.links?.[policy.links.length - 1] ?? null

  if (!activeLink) return '-'

  const start = formatDateToBR(activeLink.startDate)
  const end = activeLink.endDate ? formatDateToBR(activeLink.endDate) : 'Indeterminado'

  return `${start} à ${end}`
}
</script>
